import React from 'react';
import R from 'ramda';
import { connect } from 'react-redux';
import { firstError } from 'common/validation';
import { SHOW_CANCEL_SUBMISSION_DIALOG } from 'submission/submissionActions';
import { getStep } from 'common/selectors';
import * as steps from 'common/steps';
import Autocomplete from 'general-components/Autocomplete';
import { Dropdown } from 'react-toolbox/lib/dropdown';
import { Button, IconButton } from 'react-toolbox/lib/button';
import { Input } from 'react-toolbox/lib/input';
import LoadingPanel from 'general-components/LoadingPanel';
import OrcidDialog from './OrcidDialog';
import StepPanelButtonBar from 'general-components/StepPanelButtonBar';
import styles from './ContactInfo.css';
import DropdownTheme from 'shared-styles/DropdownTheme.css';

import {
  ADMINISTRATIVE_AREA_CHANGED,
  CITIZENSHIP_CHANGED,
  COMPLETE_STEP,
  COUNTRY_CHANGED,
  FIELD_BLURRED,
  FIELD_CHANGED,
  SHOW_ORCID_DIALOG,
} from 'contact-info/contactInfoActions';

import {
  getAdministrativeAreas,
  getCountries,
} from 'contact-info/contactInfoReducer';

import { getErrors, getFields, getLoading } from 'common/stepReducerUtil';

const getCountryValues = R.memoize(
  R.pipe(
    R.sortBy(R.prop('name')),
    R.sortBy(R.prop('displayOrder')),
    R.reduce((acc, next) => R.assoc(next.code, next.name, acc), {}),
  ),
);

const getAdministrativeAreasValues = R.memoize(
  R.pipe(
    R.sortBy(R.prop('name')),
    R.map(({ code, name }) => ({ value: code, label: name })),
  ),
);

const orcidQueryUrl = R.memoize(
  R.pipe(
    R.filter(R.identity),
    R.join('%20'),
    param =>
      `https://orcid.org/orcid-search/quick-search/?searchQuery=${param}`,
  ),
);

const ContactInfoStepPanel = ({
  addressFields,
  administrativeAreas,
  countries,
  errors,
  fields,
  loading,
  onAdministrativeAreasChanged,
  onBlur,
  onCancel,
  onCitizenshipChanged,
  onCountrySelected,
  onFieldChange,
  onNext,
  onOrcidInfo,
}) =>
  loading ? (
    <LoadingPanel />
  ) : (
    <div className={styles.stepPanel}>
      <h1>Contact Info</h1>

      <div className={styles.row}>
        <Input
          type="text"
          label="First name"
          name="firstName"
          value={fields.firstName}
          onChange={R.partial(onFieldChange, [['firstName']])}
          error={firstError(errors, 'firstName')}
          onBlur={R.partial(onBlur, ['firstName', fields.firstName])}
        />

        <Input
          type="text"
          label="Middle name (optional)"
          name="middleName"
          value={fields.middleName}
          onChange={R.partial(onFieldChange, [['middleName']])}
          error={firstError(errors, 'middleName')}
        />

        <Input
          type="text"
          label="Last name"
          name="lastName"
          value={fields.lastName}
          onChange={R.partial(onFieldChange, [['lastName']])}
          error={firstError(errors, 'lastName')}
          onBlur={R.partial(onBlur, ['lastName', fields.lastName])}
        />
      </div>

      <div className={styles.row}>
        <Input
          type="text"
          label="ORCID (optional)"
          name="orcidId"
          value={fields.orcidId}
          maxLength={19}
          onChange={R.partial(onFieldChange, [['orcidId']])}
          error={firstError(errors, 'orcidId')}
          onBlur={R.partial(onBlur, ['orcidId', fields.orcidId])}
        />
        <IconButton icon="info" primary onClick={onOrcidInfo} />
        <OrcidDialog />
        <Button
          label="Look up"
          raised
          primary
          className={styles.orcidButton}
          disabled={R.isEmpty(fields.firstName) || R.isEmpty(fields.lastName)}
          target="_blank"
          rel="noopener noreferrer"
          href={orcidQueryUrl([
            fields.firstName,
            fields.middleName,
            fields.lastName,
          ])}
        />
        <Button
          label="Create"
          raised
          primary
          className={styles.orcidButton}
          target="_blank"
          rel="noopener noreferrer"
          href="https://orcid.org/register"
        />
      </div>

      <div className={styles.row}>
        <Input
          type="text"
          label="Institutional student ID"
          name="studentId"
          value={fields.studentId}
          onChange={R.partial(onFieldChange, [['studentId']])}
          error={firstError(errors, 'studentId')}
          onBlur={R.partial(onBlur, ['studentId', fields.studentId])}
        />
      </div>

      <div className={styles.row}>
        <Input
          type="email"
          label="Email address"
          name="email"
          value={fields.email}
          onChange={R.partial(onFieldChange, [['email']])}
          error={firstError(errors, 'email')}
          onBlur={R.partial(onBlur, ['email', fields.email])}
        />
        <Input
          type="tel"
          label="Phone number (optional)"
          name="phoneNumber"
          value={fields.phoneNumber}
          onChange={R.partial(onFieldChange, [['phoneNumber']])}
          error={firstError(errors, 'phoneNumber')}
          onBlur={R.partial(onBlur, ['phoneNumber', fields.phoneNumber])}
        />
      </div>

      <div className={styles.row}>
        <Autocomplete
          label="Country of citizenship (optional)"
          multiple={false}
          source={getCountryValues(countries)}
          showSuggestionsWhenValueIsSet={true}
          direction="down"
          name="citizenShip"
          value={R.path(['citizenShip', 'code'], fields)}
          onChange={onCitizenshipChanged}
          error={firstError(errors, 'citizenShip.code')}
          onBlur={R.partial(onBlur, ['citizenShip.code', fields.citizenShip])}
          onQueryChange={query => {
            if (R.isEmpty(query)) {
              onCitizenshipChanged('');
            }
          }}
        />
      </div>

      <h2>Permanent Address</h2>

      <div className={styles.row}>
        <Autocomplete
          label="Country"
          multiple={false}
          source={getCountryValues(countries)}
          showSuggestionsWhenValueIsSet={true}
          direction="down"
          name="countryCode"
          value={R.path(['permanentAddress', 'country', 'code'], fields)}
          onChange={onCountrySelected}
          error={firstError(errors, 'permanentAddress.country.code')}
          onBlur={R.partial(onBlur, [
            'permanentAddress.country.code',
            R.path(['permanentAddress', 'country', 'code'], fields),
          ])}
          onQueryChange={query => {
            if (R.isEmpty(query)) {
              onCountrySelected('');
            }
          }}
        />
      </div>

      <div className={styles.row}>
        <Input
          type="text"
          label="Address"
          name="addressLineOne"
          value={fields.permanentAddress.addressLineOne}
          onChange={R.partial(onFieldChange, [
            ['permanentAddress', 'addressLineOne'],
          ])}
          error={firstError(errors, 'permanentAddress.addressLineOne')}
          onBlur={R.partial(onBlur, [
            'permanentAddress.addressLineOne',
            fields.permanentAddress.addressLineOne,
          ])}
        />
      </div>
      <div className={styles.row}>
        <Input
          type="text"
          label="Address 2"
          name="addressLineTwo"
          value={fields.permanentAddress.addressLineTwo}
          onChange={R.partial(onFieldChange, [
            ['permanentAddress', 'addressLineTwo'],
          ])}
          error={firstError(errors, 'permanentAddress.addressLineTwo')}
          onBlur={R.partial(onBlur, [
            'permanentAddress.addressLineTwo',
            fields.permanentAddress.addressLineTwo,
          ])}
        />
      </div>
      <div className={styles.row}>
        <Input
          type="text"
          label="Address 3"
          name="addressLineThree"
          value={fields.permanentAddress.addressLineThree}
          onChange={R.partial(onFieldChange, [
            ['permanentAddress', 'addressLineThree'],
          ])}
          error={firstError(errors, 'permanentAddress.addressLineThree')}
          onBlur={R.partial(onBlur, [
            'permanentAddress.addressLineThree',
            fields.permanentAddress.addressLineThree,
          ])}
        />
      </div>
      <div className={styles.row}>
        <Input
          className={styles.cityField}
          type="text"
          label="City"
          name=""
          value={fields.permanentAddress.locality}
          onChange={R.partial(onFieldChange, [
            ['permanentAddress', 'locality'],
          ])}
          error={firstError(errors, 'permanentAddress.locality')}
          onBlur={R.partial(onBlur, [
            'permanentAddress.locality',
            fields.permanentAddress.locality,
          ])}
        />
        <Dropdown
          className={styles.stateField}
          auto
          label="State/Province"
          name="locality"
          source={getAdministrativeAreasValues(administrativeAreas)}
          theme={DropdownTheme}
          disabled={
            R.isNil(fields.permanentAddress.country) ||
            R.isEmpty(administrativeAreas)
          }
          value={R.path(
            ['permanentAddress', 'administrativeArea', 'code'],
            fields,
          )}
          onChange={onAdministrativeAreasChanged}
          error={firstError(errors, 'permanentAddress.administrativeArea.code')}
          // onBlur={R.partial(
          //     onBlur,
          //     ['permanentAddress.administrativeArea', fields.permanentAddress.administrativeArea]
          // )}
        />
      </div>
      <div className={styles.row}>
        <Input
          className={styles.postalCodeField}
          type="text"
          label="Zip/Postal code"
          name="postalCode"
          value={fields.permanentAddress.postalCode}
          onChange={R.partial(onFieldChange, [
            ['permanentAddress', 'postalCode'],
          ])}
          error={firstError(errors, 'permanentAddress.postalCode')}
          onBlur={R.partial(onBlur, [
            'permanentAddress.postalCode',
            fields.permanentAddress.postalCode,
          ])}
        />
      </div>
      <StepPanelButtonBar onNext={onNext} onCancel={onCancel} />
    </div>
  );

const mapState = R.pipe(
  getStep(steps.CONTACT_INFO),
  R.applySpec({
    administrativeAreas: getAdministrativeAreas,
    countries: getCountries,
    errors: getErrors,
    fields: getFields,
    loading: getLoading,
  }),
);

const mapDispatch = dispatch => ({
  onCancel: () => dispatch({ type: SHOW_CANCEL_SUBMISSION_DIALOG, show: true }),
  onOrcidInfo: () => dispatch({ type: SHOW_ORCID_DIALOG, show: true }),
  onNext: () => dispatch({ type: COMPLETE_STEP }),
  onFieldChange: (path, value) =>
    dispatch({ type: FIELD_CHANGED, path, value }),
  onCountrySelected: (countryCode, event) =>
    dispatch({ type: COUNTRY_CHANGED, countryCode }),
  onCitizenshipChanged: (countryCode, event) =>
    dispatch({ type: CITIZENSHIP_CHANGED, countryCode }),
  onAdministrativeAreasChanged: (administrativeAreaCode, event) =>
    dispatch({
      type: ADMINISTRATIVE_AREA_CHANGED,
      administrativeAreaCode,
    }),
  onBlur: (field, value) => dispatch({ type: FIELD_BLURRED, field, value }),
});

export default connect(mapState, mapDispatch)(ContactInfoStepPanel);



// WEBPACK FOOTER //
// ./src/contact-info/components/ContactInfo.js